<script setup>
import {
    computed, markRaw, ref, watch,
} from 'vue';
import { iconRegistry } from '../../../generated/icon-registry';

const props = defineProps({
    name: {
        type: String,
        required: true,
    },
    size: {
        type: String,
    },
    fixedWidth: {
        type: Boolean,
        default: false,
    },
    spin: {
        type: Boolean,
        default: false,
    },
});

const map = {
    '3xl': 'm',
    '2xl': 'm',
    xl: 's',
    l: 's',
    m: 's',
    s: 's',
    xs: 's',
    '2xs': 's',
};

const version = computed(() => map[props.size ?? 'm']);

const iconFileName = computed(() => `${props.name}--${version.value}.svg`);
const IconComponent = ref(null);

watch(iconFileName, (file) => {
    const component = iconRegistry[file] ?? null;
    IconComponent.value = component ? markRaw(component) : null;
}, { immediate: true });
</script>

<template>
    <component
        :is="IconComponent"
        class="c-appIcon"
        :class="{
            [`c-appIcon--${size}`]: size,
            [`c-appIcon--fixedWidth`]: fixedWidth,
            [`c-appIcon--spin`]: spin,
        }"
        :data-qa="iconFileName"
        aria-hidden="true"
        alt=""
    />
</template>

<style lang="scss">
@use '~@karriereat/global-styles/scss/settings/font-size' as fontSize;

.c-appIcon {
    position: relative;
    top: -0.05em;
    display: inline-block;
    width: auto;
    height: 1em;
    fill: currentColor;

    &--xs {
        // stylelint-disable-next-line property-disallowed-list
        font-size: fontSize.$k-font-size--xs;
    }

    &--s {
        // stylelint-disable-next-line property-disallowed-list
        font-size: fontSize.$k-font-size--s;
    }

    &--l {
        // stylelint-disable-next-line property-disallowed-list
        font-size: fontSize.$k-font-size--l;
    }

    &--xl {
        // stylelint-disable-next-line property-disallowed-list
        font-size: fontSize.$k-font-size--xl;
    }

    &--2xl {
        // stylelint-disable-next-line property-disallowed-list
        font-size: fontSize.$k-font-size--2xl;
    }

    // There is no `3xl` font size, but we need an `3xl` icon size.
    &--3xl {
        // stylelint-disable-next-line property-disallowed-list
        font-size: 2.75em;
    }

    &--fixedWidth {
        width: 1em;
        text-align: center;
    }

    &--spin {
        animation: spin 1s infinite steps(12);
    }

    &--fill {
        fill: currentColor;
        stroke: none;
    }

    @keyframes spin {
        100% {
            transform: rotate(360deg);
        }
    }
}
</style>
